import type { Block } from "building-types/blocks/blockTypes";
import type { Building } from "building-types/building/buildingTypes";
import type { BuildingGeomTag } from "building-types/buildingGeomTag/buildingGeomTagTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { WallOpening } from "building-types/openings/wallOpeningTypes";
import type {
  EaveExtension,
  GeneratedRoof,
  GeneratedRoofSystem,
  RoofWallFaces,
} from "building-types/roofs/generatedRoofSystemTypes";
import type { MultiGableRoofClipContext } from "building-types/roofs/multiGableRoofClipContextTypes";
import {
  DEFAULT_ROOFING_MATERIAL_EXTENSION,
  DEFAULT_ROOFING_MATERIAL_THICKNESS,
} from "building-types/roofs/roofConstants";
import type { RoofFace } from "building-types/roofs/roofFace";
import { RoofWallFramingType } from "building-types/roofs/roofWallFramingTypes";
import type { Room } from "building-types/rooms/roomTypes";
import { RoomClass } from "building-types/rooms/roomTypesEnum";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { ExteriorTrimSettings } from "building-types/settings/exteriorTrimSettings";
import type { ActiveProfileSource } from "building-types/settings/profileCatalogSettings";
import type { RoofSystemLevel } from "building-types/settings/roofTypes";
import { RoofSide } from "building-types/settings/roofTypes";
import { GableRoofType } from "building-types/settings/settingsTypesEnum";
import type { WallSettings } from "building-types/settings/wallSettings";
import type { FaceArrayViewEntity } from "building-types/views/viewEntity";
import { WallType } from "building-types/walls/wallEnums";
import { WallLayerType } from "building-types/walls/wallEnums";
import type { WallFaces } from "building-types/walls/wallFacesTypes";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import type { WallPath } from "building-types/walls/wallTypes";
import type { RWTable, Table } from "common/data/table";
import { getMaxDepthErrorHandle } from "doc/building/buildingGenerationIssuesTools";
import { isInteriorRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import type { BRep, Extrusion, Face } from "geom/brep";
import { faceAABB3, faceOrNullCopy, faceOrNullMoveAlongNormal, faceNew, facesArea } from "geom/brep";
import { tagFaces } from "geom/brep";
import {
  addPolygonsToBSP,
  buildBSPNode,
  buildEdgePolygon,
  buildPolygonsFromFaces,
  buildPolygonsFromPolyline2,
  polygonBSPNew,
} from "geom/bsp";
import type { ClipFaceContext, SplitFaceBSPOptions } from "geom/bspClipping";
import { clipFace, clipFaces, clippingNodeNew, splitFaceBSP, splitFacesBSP } from "geom/bspClipping";
import type { MaxBSPDepthErrorHandler, NodeBSP, PolygonBSP } from "geom/bspTypes";
import { Vec2PolyLine2ContainmentClass, classifyContainmentVec2PolyLine2 } from "geom/containment";
import { ANGLE_EPSILON, DIST_EPSILON, DIST_EPSILON_SQUARED, PARAM_EPSILON } from "geom/core";
import { extrudeRectWithRotation, extrusionToBRep } from "geom/extrude";
import { intersectPolyLine2PolyLine2 } from "geom/intersect";
import { line2Parameter, line2ProjectPt, line2Tangent, line2NormalSafe } from "geom/line2";
import {
  planeCopyMoved,
  planeCopyReversed,
  planeIsCoplanar,
  planeMoveAlongNormal,
  planeNewXY,
  planeNewXYNeg,
  planeNormal,
  planeProjectPolyLine3,
  planeReProjectPolyLine2,
  polyLine2OnPlane,
} from "geom/plane";
import { planeBSPByThreePoints } from "geom/planeBSP";
import { intersectBRep } from "geom/polyclip/setOps3";
import {
  polyLine2Area,
  polyLine2ByTwoPoints,
  polyLine2EdgeCount,
  polyLine2EdgeEnd,
  polyLine2EdgeStart,
  polyLine2EdgeTangent,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine3EdgeCount, polyLine3EdgeEnd, polyLine3EdgeStart } from "geom/polyLine3";
import { pushXYZ } from "geom/utils";
import { vec2Dist, vec2DistSquared, vec2Dot } from "geom/vec2";
import { vec3DistSquared } from "geom/vec3";
import { getEaveDepth } from "../../elements/roofTrimUtility";
import { getRoofEdgeWallOffsetWithLayer } from "../multigableRoofPerimeter";
import { facesToRoofFaces } from "../roofFaceNew";
import { buildDormerWallBaseBoards, buildWallBaseBoards } from "../wallBaseBoards";
import { gableWallTransferWindowsToSolidWall, makeClippedWallCapFaces } from "../walls/halfStories";
import { getWallPath } from "../walls/wall";
import { clipWallFaces, operateWallFaces } from "../walls/wallCut";
import { solidWallSubstrateLayer } from "../walls/wallLayers";
import { buildBellyBandBoard } from "./roofBellyBandBoard";
import { extrudeRoofTopFaces, extrudeRoofingMaterialFaces } from "./roofExtrusions";
import { mergeCoplanarRoofTopFaces } from "./roofFacesMerge";
import { buildOverhangClipping, collectOverhangClipping } from "./roofOverhangClippingVolumes";
import { buildRidgeCaps, buildRidgeVentRails, buildRidgeVents, clipRidges } from "./roofRidges";
import { clipRoofWallFaces, clipRoofWallFacesNoRebuild, makeRoofWallCuts } from "./roofWallFaces";
import { buildWallOverlapBSPs, getUpperFloorsRoomsBSPs } from "./roofWalls";

const EXTERIOR_WALL_AREA_INSIDE_ROOF_TOLERANCE = 1;
const HALF_STORY_WALL_CLIP_AABB_RELAX = 0.1;
const WALL_ON_PERIMETER_TOLERANCE_SQUARED = 36;
const TINY_PLANE_THICKNESS = 0.001;
const WORST_WALL_CORNER_ANGLE = Math.PI / 4;

interface RoofPerimeterEdgeForMembership {
  readonly ax: number;
  readonly ay: number;
  readonly bx: number;
  readonly by: number;
  readonly perimTangentX: number;
  readonly perimTangentY: number;
}

export type IsWallOnRoofPerimeter = (
  wallStartX: number,
  wallStartY: number,
  wallEndX: number,
  wallEndY: number,
) => boolean;

export function createRoofPerimeterMembership(wallPerimeters: readonly PolyLine2[]): IsWallOnRoofPerimeter {
  const edges: RoofPerimeterEdgeForMembership[] = [];
  for (let p = 0, pl = wallPerimeters.length; p < pl; ++p) {
    const wallPerimeter = wallPerimeters[p];
    for (let i = 0, l = polyLine2EdgeCount(wallPerimeter); i < l; ++i) {
      const [ax, ay] = polyLine2EdgeStart(wallPerimeter, i);
      const [bx, by] = polyLine2EdgeEnd(wallPerimeter, i);
      const [perimTangentX, perimTangentY] = polyLine2EdgeTangent(wallPerimeter, i);
      edges.push({ ax, ay, bx, by, perimTangentX, perimTangentY });
    }
  }

  function isWallOnPerimeter(wallStartX: number, wallStartY: number, wallEndX: number, wallEndY: number): boolean {
    const [wallTangentX, wallTangentY] = line2Tangent(wallStartX, wallStartY, wallEndX, wallEndY);

    let onPerimeter = false;
    for (let e = 0, el = edges.length; e < el; ++e) {
      const { ax, ay, bx, by, perimTangentX, perimTangentY } = edges[e];

      // Parallel (either orientation): tolerate anti-parallel wall vs perimeter tangent so perimeter
      // membership does not depend on matching segment winding conventions.
      if (Math.abs(vec2Dot(perimTangentX, perimTangentY, wallTangentX, wallTangentY)) < 1 - ANGLE_EPSILON) {
        continue;
      }

      // project the wall endpoints onto the perimeter edge line; parallel case ⇒ collinear with edge line
      const [projStartX, projStartY] = line2ProjectPt(ax, ay, bx, by, wallStartX, wallStartY);
      const [projEndX, projEndY] = line2ProjectPt(ax, ay, bx, by, wallEndX, wallEndY);

      const t0 = line2Parameter(ax, ay, bx, by, projStartX, projStartY);
      const t1 = line2Parameter(ax, ay, bx, by, projEndX, projEndY);
      if (t0 === undefined || t1 === undefined) {
        continue;
      }
      const tMin = Math.min(t0, t1);
      const tMax = Math.max(t0, t1);
      const overlapStart = Math.max(0, tMin);
      const overlapEnd = Math.min(1, tMax);
      const edgeLen = vec2Dist(ax, ay, bx, by);
      const overlapAlongEdge = (overlapEnd - overlapStart) * edgeLen;
      if (overlapAlongEdge <= PARAM_EPSILON) {
        continue;
      }

      const wallStartDistSq = vec2DistSquared(wallStartX, wallStartY, projStartX, projStartY);
      const wallEndDistSq = vec2DistSquared(wallEndX, wallEndY, projEndX, projEndY);
      const maxWallToPerimeterLineDistSq = Math.max(wallStartDistSq, wallEndDistSq);

      if (maxWallToPerimeterLineDistSq <= WALL_ON_PERIMETER_TOLERANCE_SQUARED) {
        onPerimeter = true;
        break;
      }
    }

    return onPerimeter;
  }

  return isWallOnPerimeter;
}

export function isPorchAdjacentWall(leftRoom: Room | null, rightRoom: Room | null): boolean {
  return leftRoom?.attributes.roomClass === RoomClass.PORCH || rightRoom?.attributes.roomClass === RoomClass.PORCH;
}

function wallClipSolidBrepsWithRelaxation(level: Level, path: WallPath): readonly [BRep, BRep, BRep] | null {
  const planeNormal = line2NormalSafe(path.startX, path.startY, path.endX, path.endY);

  if (!planeNormal) {
    return null;
  }

  const zLo = level.floorElevation;
  const zHi = level.floorElevation + Math.max(level.topOfWall, DIST_EPSILON);
  const height = zHi - zLo;

  if (height <= DIST_EPSILON) {
    return null;
  }

  const dx = path.endX - path.startX;
  const dy = path.endY - path.startY;
  const segLen = vec2Dist(path.startX, path.startY, path.endX, path.endY);
  const reducedSegLen = segLen - (2 * HALF_STORY_WALL_CLIP_AABB_RELAX) / Math.tan(0.5 * WORST_WALL_CORNER_ANGLE);

  if (reducedSegLen <= DIST_EPSILON) {
    return null;
  }

  const midX = (path.startX + path.endX) * 0.5;
  const midY = (path.startY + path.endY) * 0.5;
  const rotation = Math.atan2(dy, dx);

  const [nx, ny] = planeNormal;
  const ox = nx * HALF_STORY_WALL_CLIP_AABB_RELAX;
  const oy = ny * HALF_STORY_WALL_CLIP_AABB_RELAX;

  const extrusion0 = extrudeRectWithRotation(midX, midY, zLo, height, segLen, TINY_PLANE_THICKNESS, rotation);
  const extrusion1 = extrudeRectWithRotation(midX + ox, midY + oy, zLo, height, segLen, TINY_PLANE_THICKNESS, rotation);
  const extrusion2 = extrudeRectWithRotation(midX - ox, midY - oy, zLo, height, segLen, TINY_PLANE_THICKNESS, rotation);

  return [extrusionToBRep(extrusion0), extrusionToBRep(extrusion1), extrusionToBRep(extrusion2)];
}

function brepIntersectionNonEmpty(a: BRep, b: BRep): boolean {
  return intersectBRep(a, b).length > 0;
}

function roofWallClipIntersectsRoofTopSolidsWithRelaxation(
  wallBreps: readonly [BRep, BRep, BRep],
  roofTopPlates: readonly Extrusion[],
): boolean {
  if (roofTopPlates.length === 0) {
    return false;
  }

  const roofCandidateBreps: BRep[] = [];

  for (let i = 0, l = roofTopPlates.length; i < l; i++) {
    roofCandidateBreps.push(extrusionToBRep(roofTopPlates[i]));
  }

  const [w0, w1, w2] = wallBreps;

  for (let i = 0, l = roofCandidateBreps.length; i < l; i++) {
    const roofBrep = roofCandidateBreps[i];

    if (
      brepIntersectionNonEmpty(w0, roofBrep) &&
      brepIntersectionNonEmpty(w1, roofBrep) &&
      brepIntersectionNonEmpty(w2, roofBrep)
    ) {
      return true;
    }
  }

  return false;
}

function wallSegmentAssociatesSubRoofFootprint(path: WallPath, wallPerimeter: PolyLine2): boolean {
  const midX = (path.startX + path.endX) / 2;
  const midY = (path.startY + path.endY) / 2;

  const s = classifyContainmentVec2PolyLine2(path.startX, path.startY, wallPerimeter);
  const m = classifyContainmentVec2PolyLine2(midX, midY, wallPerimeter);
  const e = classifyContainmentVec2PolyLine2(path.endX, path.endY, wallPerimeter);

  if (
    s !== Vec2PolyLine2ContainmentClass.OUTSIDE ||
    m !== Vec2PolyLine2ContainmentClass.OUTSIDE ||
    e !== Vec2PolyLine2ContainmentClass.OUTSIDE
  ) {
    return true;
  }

  const wallSeg = polyLine2ByTwoPoints(path.startX, path.startY, path.endX, path.endY);
  return intersectPolyLine2PolyLine2(wallSeg, wallPerimeter, DIST_EPSILON).length > 0;
}

/**
 *  A wall can sit under or span multiple sub-roofs (e.g. angled junctions);
 * all matching BSPs are re-applied on the second exterior pass.
 *
 */
export function resolveHalfStoryDiscardBspsForWall(
  level: Level,
  path: WallPath,
  subRoofs: Table<GeneratedRoof>,
  discardHalfStoryWallBSPsTable: Table<NodeBSP>,
): NodeBSP[] {
  const sortedSubRoofIds = Object.keys(discardHalfStoryWallBSPsTable).sort();
  const out: NodeBSP[] = [];
  const wallBreps = wallClipSolidBrepsWithRelaxation(level, path);

  for (let i = 0, l = sortedSubRoofIds.length; i < l; i++) {
    const subRoofId = sortedSubRoofIds[i];
    const bsp = discardHalfStoryWallBSPsTable[subRoofId];
    const subRoof = subRoofs[subRoofId];

    if (!subRoof || !bsp) {
      continue;
    }

    const plates = subRoof.roofTopPlates;
    const useGeomOverlapFilter = (plates?.length ?? 0) > 0;

    const roofLevelMatchesWallStack =
      subRoof.levelId === level.id || (level.levelBelow !== undefined && subRoof.levelId === level.levelBelow);

    if (useGeomOverlapFilter) {
      if (!wallBreps) {
        continue;
      }

      if (!roofWallClipIntersectsRoofTopSolidsWithRelaxation(wallBreps, plates ?? [])) {
        continue;
      }
    } else if (!roofLevelMatchesWallStack) {
      continue;
    }

    const { wallPerimeter } = subRoof.roofVolume.perimeters;

    if (!wallSegmentAssociatesSubRoofFootprint(path, wallPerimeter)) {
      continue;
    }

    out.push(bsp);
  }

  return out;
}

function hasSomeVoidWallFraming(roof: GeneratedRoof): boolean {
  return roof.roofVolume.exactEdges.some((x) => x.framingType === RoofWallFramingType.NONE);
}

function buildRoofVolumeBSPs(roofs: Table<GeneratedRoof>, issues: BuildingGenerationIssue[]): (NodeBSP | null)[] {
  return Object.values(roofs).map((x) => buildRoofVolumeBSP(x, issues));
}

function buildRoofVolumePolygons(roof: GeneratedRoof, issues: BuildingGenerationIssue[]): PolygonBSP[] | null {
  const { roofVolume, roofSettings, polygonId, roofTopFaces } = roof;
  const { perimeters, baseHeight, exactEdges, startFaces, endFaces } = roofVolume;
  const { wallPerimeter } = perimeters;
  const { face: startFrontFace } = startFaces;
  const { face: endFrontFace } = endFaces;

  const maxDepthErrorHandle = getMaxDepthErrorHandle(issues, { type: "ROOF", id: roof.id });

  if (hasSomeVoidWallFraming(roof) || polygonId < 0) {
    return null;
  }

  let baseZ = baseHeight - getEaveDepth(roofSettings);

  const updateBaseZ = (face: Face | null) => {
    if (!face) {
      return;
    }

    const aabb = faceAABB3(face);

    baseZ = Math.max(baseZ, aabb.minZ);
  };

  updateBaseZ(startFrontFace);
  updateBaseZ(endFrontFace);

  const wallPerimter3 = polyLine2OnPlane(planeNewXY(0, 0, baseZ), wallPerimeter);
  const basePlane = planeNewXYNeg(0, 0, baseZ);
  const baseFace = faceNew([planeProjectPolyLine3(basePlane, wallPerimter3)], basePlane);
  const basePolygons = buildPolygonsFromFaces({ faces: [baseFace] });
  const premiterPolygons: PolygonBSP[] = [...basePolygons];
  const edgeCount = polyLine2EdgeCount(wallPerimeter);
  for (let i = 0, l = edgeCount; i < l; i++) {
    const { isStartEdge, isEndEdge, fullyTruncated } = exactEdges[i];
    if (((isStartEdge && !startFrontFace) || (isEndEdge && !endFrontFace)) && !fullyTruncated) {
      continue;
    }

    const componentIndex = i * 2;

    const ax = wallPerimeter[componentIndex];
    const ay = wallPerimeter[componentIndex + 1];
    const bx = wallPerimeter[componentIndex + 2];
    const by = wallPerimeter[componentIndex + 3];

    const edgePolygon = buildEdgePolygon(ax, ay, baseZ, bx, by, baseZ, 0, 0, 1);

    if (!edgePolygon) {
      continue;
    }

    premiterPolygons.push(edgePolygon);
  }

  const perimeterBSP = buildBSPNode({
    polygons: premiterPolygons,
    epsilon: MULTIGABLE_BSP_EPSILON,
    maxDepthErrorHandle,
  });

  const finalPolygons: PolygonBSP[] = [];
  const addPolygonFromVertices = (vertices: number[]) => {
    if (vertices.length < 9) {
      return;
    }

    const edgePlane = planeBSPByThreePoints(
      vertices[0],
      vertices[1],
      vertices[2],
      vertices[3],
      vertices[4],
      vertices[5],
      vertices[6],
      vertices[7],
      vertices[8],
    );

    if (!edgePlane) {
      return;
    }

    const edgePolygon = polygonBSPNew(0, vertices, edgePlane);

    finalPolygons.push(edgePolygon);
  };

  const addTopFace = (face: Face) => {
    const insideFaces: Face[] = [];
    splitFaceBSP(face, {
      node: perimeterBSP,
      coplanarsToFront: true,
      backFaces: insideFaces,
      epsilon: MULTIGABLE_BSP_EPSILON,
    });

    buildPolygonsFromFaces({ faces: insideFaces, polygons: finalPolygons });

    for (const insideFace of insideFaces) {
      const { loops, plane } = insideFace;
      if (loops.length === 0) {
        continue;
      }

      const pl3 = polyLine2OnPlane(plane, loops[0]);
      const edgeCount = polyLine3EdgeCount(pl3);
      for (let i = 0; i < edgeCount; i++) {
        const edgeVertices: number[] = [];

        const [x0, y0, z0] = polyLine3EdgeStart(pl3, i);
        const [x1, y1, z1] = polyLine3EdgeEnd(pl3, i);

        if (vec3DistSquared(x0, y0, z0, x1, y1, z1) < DIST_EPSILON_SQUARED) {
          continue;
        }

        pushXYZ(edgeVertices, x0, y0, z0);
        pushXYZ(edgeVertices, x1, y1, z1);

        if (Math.abs(z1 - baseZ) > DIST_EPSILON) {
          pushXYZ(edgeVertices, x1, y1, baseZ);
        }

        if (Math.abs(z0 - baseZ) > DIST_EPSILON) {
          pushXYZ(edgeVertices, x0, y0, baseZ);
        }

        addPolygonFromVertices(edgeVertices);
      }

      const bottomVertices: number[] = [];
      for (let i = 0; i < edgeCount; i++) {
        const [x, y] = polyLine3EdgeStart(pl3, i);
        pushXYZ(bottomVertices, x, y, baseZ);
      }

      addPolygonFromVertices(bottomVertices);
    }
  };

  for (const roofTopFace of roofTopFaces) {
    addTopFace(roofTopFace.face);
  }

  return finalPolygons;
}

function buildRoofVolumeBSP(roof: GeneratedRoof, issues: BuildingGenerationIssue[]): NodeBSP | null {
  const polygons = buildRoofVolumePolygons(roof, issues);
  if (!polygons) {
    return null;
  }

  const maxDepthErrorHandle = getMaxDepthErrorHandle(issues, { type: "ROOF", id: roof.id });
  return buildBSPNode({
    polygons,
    epsilon: MULTIGABLE_BSP_EPSILON,
    maxDepthErrorHandle,
  });
}

const tagClipWallSystem: BuildingGeomTag = { type: "OPERATION", operation: "CLIP_WALL_SYSTEM" };

function clipWallFacesWithHalfStoryDiscard(wallFaces: WallFaces, discardHalfStoryWallBSPs: NodeBSP[]): void {
  const discardOptions: ClipFaceContext = {
    clippingNodes: discardHalfStoryWallBSPs.map((x) => clippingNodeNew(x)),
    checkCoplanarFacing: true,
    epsilon: MULTIGABLE_BSP_EPSILON,
  };
  clipWallFaces(wallFaces, discardOptions);
}

/**
 * We need to protect the exterior faces of the wall from being clipped by the half-story discard BSPs.
 * However, if there is a interior wall that is inside the roof volume, we need to clip it
 * by the half-story discard BSPs.
 *
 */
function clipHalfStoryWallFacesWithDiscardNoShedSplit(
  level: Level,
  wallFaces: WallFaces,
  path: WallPath,
  subRoofs: Table<GeneratedRoof>,
  discardHalfStoryWallBSPs: NodeBSP[],
  discardHalfStoryWallBSPsTable: Table<NodeBSP>,
  discardNoShedOrDormersBSP: NodeBSP,
  isWallOnRoofPerimeter: IsWallOnRoofPerimeter,
): void {
  const halfStoryOptions: ClipFaceContext = {
    clippingNodes: discardHalfStoryWallBSPs.map((x) => clippingNodeNew(x)),
    checkCoplanarFacing: true,
    epsilon: MULTIGABLE_BSP_EPSILON,
  };

  const isInteriorWallInsideRoofVolume = !isWallOnRoofPerimeter(path.startX, path.startY, path.endX, path.endY);

  const clipPred = (faces: Face[]): Face[] => {
    const result: Face[] = [];
    for (const face of faces) {
      if (isInteriorWallInsideRoofVolume) {
        //clip the face by the half-story discard BSPs if it is inside the roof volume
        const clipped = clipFaces({
          faces: [face],
          options: halfStoryOptions,
        });
        result.push(...clipped);
      } else {
        //protect the exterior faces of the wall from being clipped by the discard-no-shed/dormer BSP
        //and clip the interior faces by the half-story discard BSPs
        const insideFaces: Face[] = clipFaces({
          faces: [face],
          options: {
            clippingNodes: [clippingNodeNew(discardNoShedOrDormersBSP, true)],
            checkCoplanarFacing: true,
            epsilon: MULTIGABLE_BSP_EPSILON,
          },
        });
        const outsideFaces: Face[] = clipFaces({
          faces: [face],
          options: {
            clippingNodes: [clippingNodeNew(discardNoShedOrDormersBSP)],
            checkCoplanarFacing: true,
            epsilon: MULTIGABLE_BSP_EPSILON,
          },
        });
        const clippedOutside: Face[] = clipFaces({
          faces: outsideFaces,
          options: halfStoryOptions,
        });
        result.push(...insideFaces);
        result.push(...clippedOutside);
      }
    }
    return result;
  };

  //this operation will leave the exterior faces or even interior faces if they are inside the roof volume and
  //not on the roof perimeter and even for some roof perimeter ones which must be clipped by their respective sub-roof
  // half-story discard BSPs and that is solved in the scopedHalfStoryBsps operation below.
  operateWallFaces(wallFaces, clipPred, false);

  if (!isInteriorWallInsideRoofVolume) {
    const scopedHalfStoryBsps = resolveHalfStoryDiscardBspsForWall(
      level,
      path,
      subRoofs,
      discardHalfStoryWallBSPsTable,
    );
    if (scopedHalfStoryBsps.length > 0) {
      const secondPassHalfStoryOptions: ClipFaceContext = {
        clippingNodes: scopedHalfStoryBsps.map((node) => clippingNodeNew(node)),
        checkCoplanarFacing: true,
        epsilon: MULTIGABLE_BSP_EPSILON,
      };
      const secondPassClipPred = (faces: Face[]): Face[] =>
        clipFaces({
          faces,
          options: secondPassHalfStoryOptions,
        });
      operateWallFaces(wallFaces, secondPassClipPred, false);
    }
  }
}

export function clipWallSystem(
  level: Level,
  wallSystem: WallSystem,
  roofs: Table<GeneratedRoofSystem>,
  roofSystemsHalfStoryClipping: RWTable<Table<NodeBSP>>,
  blockIsHalfStory: Table<boolean>,
  rooms: Table<Room>,
  wallSettings: WallSettings,
  issues: BuildingGenerationIssue[],
): WallSystem {
  const { floorIndex } = level;
  const { exteriorWallSubstrateThickness } = wallSettings;

  for (const roofId in roofs) {
    const roof = roofs[roofId];

    // only working for multi-gables atm
    const { clipContext, roofs: subRoofs } = roof;
    const { insideBSP, discardNoShedOrDormersBSP } = clipContext;
    const discardHalfStoryWallBSPsTable = roofSystemsHalfStoryClipping[roofId];

    const discardHalfStoryWallBSPs: NodeBSP[] = [];
    for (const subRoofId in discardHalfStoryWallBSPsTable) {
      discardHalfStoryWallBSPs.push(discardHalfStoryWallBSPsTable[subRoofId]);
    }

    const insideBSPs = buildRoofVolumeBSPs(subRoofs, issues);
    const subRoofWallPerimeters: PolyLine2[] = [];
    for (const subRoofId in subRoofs) {
      subRoofWallPerimeters.push(subRoofs[subRoofId].roofVolume.perimeters.wallPerimeter);
    }
    const isWallOnRoofPerimeter = createRoofPerimeterMembership(subRoofWallPerimeters);

    for (const id in wallSystem.walls) {
      const wall = wallSystem.walls[id];

      /**
       * @TODO - void walls can have solid wall sections, but they are not clipped by the
       * roof system due to this check and the inability of the VOID walls to store WallFaces.
       * This needs to be updated
       */
      // ignore void walls
      if (wall.wallType !== WallType.SOLID) {
        continue;
      }

      const { wallFaces } = wall;
      const path = getWallPath(wall);
      const { leftEdge, rightEdge, leftFace, rightFace, thickness } = solidWallSubstrateLayer(wall);
      const { leftRoom: leftRoomLike, rightRoom: rightRoomLike } = path;

      const leftRoom = leftRoomLike ? rooms[leftRoomLike.id] : null;
      const rightRoom = rightRoomLike ? rooms[rightRoomLike.id] : null;

      const isLeftHalfStoryOrUndefined = leftRoom ? blockIsHalfStory[leftRoom.parentId] : true;
      const isRightHalfStoryOrUndefined = rightRoom ? blockIsHalfStory[rightRoom.parentId] : true;

      const leftRoomEnclosure = leftRoom?.attributes.roomEnclosure;
      const rightRoomEnclosure = rightRoom?.attributes.roomEnclosure;

      const isLeftRoomInterior = leftRoomEnclosure
        ? isInteriorRoomEnclosure[leftRoom?.attributes.roomEnclosure]
        : false;
      const isRightRoomInterior = rightRoomEnclosure
        ? isInteriorRoomEnclosure[rightRoom?.attributes.roomEnclosure]
        : false;

      // if both rooms are half-stories or this is a half-story boundary
      if (isLeftHalfStoryOrUndefined && isRightHalfStoryOrUndefined) {
        if (discardHalfStoryWallBSPs.length > 0) {
          const isPorchAdjacent = isPorchAdjacentWall(leftRoom, rightRoom);
          const useDiscardNoShedSplitPath = floorIndex > 0 && discardNoShedOrDormersBSP && !isPorchAdjacent;

          if (useDiscardNoShedSplitPath) {
            clipHalfStoryWallFacesWithDiscardNoShedSplit(
              level,
              wallFaces,
              path,
              subRoofs,
              discardHalfStoryWallBSPs,
              discardHalfStoryWallBSPsTable,
              discardNoShedOrDormersBSP,
              isWallOnRoofPerimeter,
            );
          } else {
            clipWallFacesWithHalfStoryDiscard(wallFaces, discardHalfStoryWallBSPs);
          }
        }
      } else if (isLeftHalfStoryOrUndefined && isLeftRoomInterior) {
        // if only left side is a half-story and interior,
        // otherwise it's already exterior

        // split the left face
        const splitOptions: SplitFaceBSPOptions = {
          node: insideBSP,
          coplanarsToFront: true,
          frontFaces: wallFaces.leftExteriorFaces,
          epsilon: MULTIGABLE_BSP_EPSILON,
        };

        const leftWallFaces = wallFaces.leftFaces.map((x) =>
          faceOrNullMoveAlongNormal(faceOrNullCopy(x), exteriorWallSubstrateThickness),
        );

        splitFacesBSP(
          leftWallFaces.filter((face) => face !== null),
          splitOptions,
        );

        // generate the caps
        wallFaces.leftExteriorFaces.push(
          ...tagFaces(
            makeClippedWallCapFaces(thickness, exteriorWallSubstrateThickness, leftEdge, insideBSP, level),
            tagClipWallSystem,
          ),
        );
      } else if (isRightHalfStoryOrUndefined && isRightRoomInterior) {
        // if only right side is a half-story and interior,
        // otherwise it's already exterior

        // split the right face
        const splitOptions: SplitFaceBSPOptions = {
          node: insideBSP,
          coplanarsToFront: true,
          frontFaces: wallFaces.rightExteriorFaces,
          epsilon: MULTIGABLE_BSP_EPSILON,
        };

        const rightWallFaces = wallFaces.rightFaces.map((x) =>
          faceOrNullMoveAlongNormal(faceOrNullCopy(x), exteriorWallSubstrateThickness),
        );

        splitFacesBSP(
          rightWallFaces.filter((face) => face !== null),
          splitOptions,
        );

        // generate the caps
        wallFaces.rightExteriorFaces.push(
          ...tagFaces(
            makeClippedWallCapFaces(thickness, exteriorWallSubstrateThickness, rightEdge, insideBSP, level),
            tagClipWallSystem,
          ),
        );
      } else if (isRightRoomInterior && isLeftHalfStoryOrUndefined && floorIndex > 0) {
        const exteriorOptions: ClipFaceContext = {
          clippingNodes: insideBSPs.map((x) => clippingNodeNew(x)),
          epsilon: MULTIGABLE_BSP_EPSILON,
        };

        const newFaces = tagFaces(
          clipFaces({
            faces: wallFaces.leftFaces,
            options: exteriorOptions,
            normalOffset: 2 * MULTIGABLE_BSP_EPSILON,
          }),
          tagClipWallSystem,
        );

        const newArea = facesArea(newFaces);
        const oldArea = facesArea(wallFaces.leftFaces);

        if (Math.abs(newArea - oldArea) > EXTERIOR_WALL_AREA_INSIDE_ROOF_TOLERANCE) {
          wallFaces.leftFaces = newFaces;
        }
      } else if (isLeftRoomInterior && isRightHalfStoryOrUndefined && floorIndex > 0) {
        const exteriorOptions: ClipFaceContext = {
          clippingNodes: insideBSPs.map((x) => clippingNodeNew(x)),
          epsilon: MULTIGABLE_BSP_EPSILON,
        };

        const newFaces = tagFaces(
          clipFaces({
            faces: wallFaces.rightFaces,
            options: exteriorOptions,
            normalOffset: 2 * MULTIGABLE_BSP_EPSILON,
          }),
          tagClipWallSystem,
        );

        const newArea = facesArea(newFaces);
        const oldArea = facesArea(wallFaces.rightFaces);

        if (Math.abs(newArea - oldArea) > EXTERIOR_WALL_AREA_INSIDE_ROOF_TOLERANCE) {
          wallFaces.rightFaces = newFaces;
        }
      }

      for (const subRoofId in subRoofs) {
        const { roofVolume, startWindows, endWindows } = subRoofs[subRoofId];
        const { startFaces, endFaces } = roofVolume;
        const { face: startFrontFace } = startFaces;
        const { face: endFrontFace } = endFaces;

        if (endFrontFace && planeIsCoplanar(endFrontFace.plane, leftFace.plane)) {
          gableWallTransferWindowsToSolidWall(endFrontFace.plane, endWindows, wall, false);
        } else if (endFrontFace && planeIsCoplanar(endFrontFace.plane, rightFace.plane)) {
          gableWallTransferWindowsToSolidWall(endFrontFace.plane, endWindows, wall, true);
        } else if (startFrontFace && planeIsCoplanar(startFrontFace.plane, leftFace.plane)) {
          gableWallTransferWindowsToSolidWall(startFrontFace.plane, startWindows, wall, false);
        } else if (startFrontFace && planeIsCoplanar(startFrontFace.plane, rightFace.plane)) {
          gableWallTransferWindowsToSolidWall(startFrontFace.plane, startWindows, wall, true);
        }
      }
    }
  }

  return wallSystem;
}

interface ClipRoofOptions {
  readonly roofSystem: GeneratedRoofSystem;
  readonly dormers: Building["dormers"];
  readonly levels: Building["levels"];
  readonly roofSystemLevels: Building["roofSystemLevels"];
  readonly walls: Building["walls"];
  readonly roofWallCuts: WallOpening[];
  readonly roofWallDiscardBSPs: Table<NodeBSP>;
  readonly wallSettings: WallSettings;
  readonly profileSource: ActiveProfileSource;
  readonly exteriorTrimSettings: ExteriorTrimSettings;
  readonly blocks: Table<Block>;
  readonly wallOverlapForFriezeBoardBSPs: NodeBSP[];
  readonly gableWallOverlapBSPs: NodeBSP[];
  readonly issues: BuildingGenerationIssue[];
}

export function clipRoofSystem({
  roofSystem,
  dormers,
  levels,
  roofSystemLevels,
  walls,
  roofWallCuts,
  roofWallDiscardBSPs,
  wallSettings,
  profileSource,
  exteriorTrimSettings,
  blocks,
  wallOverlapForFriezeBoardBSPs,
  gableWallOverlapBSPs,
  issues,
}: ClipRoofOptions): GeneratedRoofSystem {
  const { roofs, clipContext } = roofSystem;
  const { insideBSP } = clipContext;

  const wallOverlapBSPs = buildWallOverlapBSPs(walls, blocks, insideBSP, false);
  const newSubRoofs = buildOverhangClipping(roofs, clipContext);

  for (const subRoofId in newSubRoofs) {
    const subRoof = newSubRoofs[subRoofId];
    const roofWallDiscardBSP = roofWallDiscardBSPs[subRoof.levelId];
    const maxDepthErrorHandle = getMaxDepthErrorHandle(issues, { type: "ROOF", id: subRoof.id });

    newSubRoofs[subRoofId] = clipGableSubRoof({
      levels,
      roofSystemLevels,
      subRoof,
      clipContext,
      subRoofs: newSubRoofs,
      walls,
      roofWallCuts,
      wallSettings,
      profileSource,
      exteriorTrimSettings,
      wallOverlapBSPs,
      wallOverlapForFriezeBoardBSPs,
      gableWallOverlapBSPs,
      roofWallDiscardBSP,
      maxDepthErrorHandle,
      issues,
    });
  }

  for (const dormerId in dormers) {
    if (dormers[dormerId].hostRoofSystemId !== roofSystem.id) {
      continue;
    }
    const { wallBaseBoardRails, wallBaseBoards, pickHandle } = dormers[dormerId];
    buildDormerWallBaseBoards({
      wallBaseBoardRails,
      subRoofs: newSubRoofs,
      profileSource,
      clipContext,
      wallBaseBoards,
      pickHandle,
      issues,
    });
  }

  return {
    ...roofSystem,
    roofs: newSubRoofs,
  };
}

export function clipRoofFaces(
  roofFaces: RoofFace[],
  options: ClipFaceContext,
  filterFunc?: (x: RoofFace) => boolean,
): RoofFace[] {
  const clippedRoofFaces: RoofFace[] = [];
  for (let i = 0, l = roofFaces.length; i < l; i++) {
    const roofFace = roofFaces[i];

    if (!filterFunc || filterFunc(roofFace)) {
      const { face, side, type, isDormer, annotationSettings, disableOverhangClipping } = roofFace;

      const clippedFaces = clipFace({
        face,
        options,
      });

      clippedRoofFaces.push(
        ...facesToRoofFaces(clippedFaces, type, side, isDormer, annotationSettings, disableOverhangClipping),
      );
    } else {
      clippedRoofFaces.push(roofFace);
    }
  }

  return clippedRoofFaces;
}

function clipGableWallFaces(
  wallFaces: RoofWallFaces,
  roofWallCuts: WallOpening[],
  insideBSP: NodeBSP | null,
  discardBSP: NodeBSP | null,
  roofWallDiscardBSP: NodeBSP,
  wallOverlapBSPs: NodeBSP[],
  upperFloorsRoomsBSPs: NodeBSP[],
  roofType: GableRoofType,
  limitBSP: NodeBSP | null = null,
) {
  const options1: ClipFaceContext = {
    clippingNodes: [
      clippingNodeNew(limitBSP),
      clippingNodeNew(insideBSP),
      ...upperFloorsRoomsBSPs.map((x) => clippingNodeNew(x)),
    ],
    coplanarsToFront: true,
    epsilon: MULTIGABLE_BSP_EPSILON,
  };

  const options2: ClipFaceContext = {
    clippingNodes: [...wallOverlapBSPs.map((x) => clippingNodeNew(x)), clippingNodeNew(roofWallDiscardBSP)],
    coplanarsToFront: true,
    checkCoplanarFacing: true,
    epsilon: MULTIGABLE_BSP_EPSILON,
  };

  const options3: ClipFaceContext = {
    clippingNodes: [clippingNodeNew(discardBSP, true)],
    coplanarsToFront: false,
    epsilon: MULTIGABLE_BSP_EPSILON,
  };

  makeRoofWallCuts({
    roofWallCuts,
    wallFaces,
  });

  clipRoofWallFaces(wallFaces, options1);
  clipRoofWallFaces(wallFaces, options2);

  if (roofType !== GableRoofType.DEFAULT) {
    clipRoofWallFacesNoRebuild(wallFaces, options3);
  }
}

interface ClipGableSubRoofOptions {
  readonly levels: Table<Level>;
  readonly roofSystemLevels: Table<RoofSystemLevel>;
  readonly subRoof: GeneratedRoof;
  readonly clipContext: MultiGableRoofClipContext;
  readonly subRoofs: Table<GeneratedRoof>;
  readonly walls: Building["walls"];
  readonly roofWallCuts: WallOpening[];
  readonly wallSettings: WallSettings;
  readonly profileSource: ActiveProfileSource;
  readonly exteriorTrimSettings: ExteriorTrimSettings;
  readonly wallOverlapBSPs: NodeBSP[];
  readonly wallOverlapForFriezeBoardBSPs: NodeBSP[];
  readonly gableWallOverlapBSPs: NodeBSP[];
  readonly roofWallDiscardBSP: NodeBSP;
  readonly maxDepthErrorHandle: MaxBSPDepthErrorHandler;
  readonly issues: BuildingGenerationIssue[];
}

function clipGableSubRoof({
  levels,
  roofSystemLevels,
  subRoof,
  clipContext,
  subRoofs,
  walls,
  roofWallCuts,
  wallSettings,
  profileSource,
  exteriorTrimSettings,
  wallOverlapBSPs,
  wallOverlapForFriezeBoardBSPs,
  gableWallOverlapBSPs,
  roofWallDiscardBSP,
  maxDepthErrorHandle,
  issues,
}: ClipGableSubRoofOptions): GeneratedRoof {
  const { insideBSP, discardBSP } = clipContext;
  const {
    roofVolume,
    wallFaces,
    rails,
    roofType,
    roofSettings,
    isDormer,
    roofPerimeterRails,
    roofWallSystem,
    bellyBandBoardRails,
  } = subRoof;
  const { limitBSP, neighborRoofs, edges } = roofVolume;
  const {
    roofingMaterialThickness = DEFAULT_ROOFING_MATERIAL_THICKNESS,
    roofingMaterialExtension = DEFAULT_ROOFING_MATERIAL_EXTENSION,
  } = roofSettings;
  const { ridgeRails } = rails;
  const { startWallFaces, endWallFaces } = wallFaces;

  const upperFloorsRoomsBSPs = getUpperFloorsRoomsBSPs(levels, levels[subRoof.levelId], roofSystemLevels);

  // clip the roof top faces against the whole roof system
  const clipOptions: ClipFaceContext = {
    clippingNodes: [clippingNodeNew(insideBSP), ...upperFloorsRoomsBSPs.map((x) => clippingNodeNew(x))],
    coplanarsToFront: true,
    epsilon: MULTIGABLE_BSP_EPSILON,
  };

  let roofTopFaces = clipRoofFaces(subRoof.roofTopFaces, clipOptions);

  // clip bottom faces
  let bottomFaces = subRoof.bottomFaces;
  for (const otherSubRoofId in subRoofs) {
    const otherSubRoof = subRoofs[otherSubRoofId];

    if (subRoof.index !== otherSubRoof.index) {
      const bottomFacesOptions: ClipFaceContext = {
        clippingNodes: [clippingNodeNew(otherSubRoof.roofVolume.clipBottomBSP)],
        coplanarsToFront: true,
        epsilon: MULTIGABLE_BSP_EPSILON,
      };

      bottomFaces = clipFaces({
        faces: bottomFaces,
        options: bottomFacesOptions,
      });
    }
  }

  // clip unwanted overhangs
  const overhangClippingResult = performOverhangClipping({
    subRoof,
    subRoofs,
    roofTopFaces,
  });

  const { startEaveExtensions, endEaveExtensions, roofTopClippingBSPs } = overhangClippingResult;
  roofTopFaces = overhangClippingResult.roofTopFaces;

  // add the extensions roof tops
  for (const { roofTopFace } of startEaveExtensions) {
    roofTopFaces.push(roofTopFace);
  }

  for (const { roofTopFace } of endEaveExtensions) {
    roofTopFaces.push(roofTopFace);
  }

  // merge coplanar faces
  roofTopFaces = mergeCoplanarRoofTopFaces(roofTopFaces);

  const roofTopPlates = extrudeRoofTopFaces(roofTopFaces, roofPerimeterRails);
  const roofingMaterialFaces = extrudeRoofingMaterialFaces(
    roofTopFaces,
    roofingMaterialThickness,
    roofingMaterialExtension,
    roofVolume.exactEdges,
    maxDepthErrorHandle,
  );

  // build belly band boards
  const bellyBandBoards: FaceArrayViewEntity[] = [];
  for (const bellyBandBoard of bellyBandBoardRails) {
    const edgeInfo = edges.find((edge) => edge.side === bellyBandBoard.roofSide);

    if (!edgeInfo) {
      continue;
    }

    let { exteriorWallOffsetWithFinish } = edgeInfo;

    if (roofWallSystem) {
      const { wallMassSystem, planarGraph } = roofWallSystem;
      exteriorWallOffsetWithFinish = getRoofEdgeWallOffsetWithLayer({
        wallMassSystem,
        planarGraph: planarGraph.planarGraph,
        edge: edgeInfo.edge,
        wallSettings,
        wallLayerType: WallLayerType.FINISH,
      });
    }

    buildBellyBandBoard({
      bellyBandBoard,
      profileSource,
      clipContext,
      wallOverlapForFriezeBoardBSPs,
      gableWallOverlapBSPs,
      bellyBandBoards,
      planeOffset: exteriorWallOffsetWithFinish - edgeInfo.exteriorWallOffsetWithFinish,
      issues,
      handle: { type: "ROOF", id: subRoof.id },
    });
  }

  clipGableWallFaces(
    startWallFaces,
    roofWallCuts,
    insideBSP,
    discardBSP,
    roofWallDiscardBSP,
    wallOverlapBSPs,
    upperFloorsRoomsBSPs,
    roofType,
    limitBSP,
  );

  clipGableWallFaces(
    endWallFaces,
    roofWallCuts,
    insideBSP,
    discardBSP,
    roofWallDiscardBSP,
    wallOverlapBSPs,
    upperFloorsRoomsBSPs,
    roofType,
    limitBSP,
  );

  // build wall base boards
  const { wallBaseBoards, wallBaseBoardRails } = buildWallBaseBoards({
    subRoof,
    subRoofs,
    roofTopFaces,
    profileSource,
    exteriorTrimSettings,
    clipContext,
    walls,
    isDormer,
    issues,
  });

  // clip ridges
  clipRidges({
    subRoof,
    limitBSP,
    clipContext,
    ridgeRails,
    roofTopClippingBSPs,
  });

  buildRidgeVentRails(subRoofs, ridgeRails, roofSettings, wallSettings, neighborRoofs);

  // build ridge vents
  const ridgeVentFaces = buildRidgeVents(ridgeRails);

  // build ridge caps
  const ridgeCapFaces = buildRidgeCaps(ridgeRails, clipContext);

  return {
    ...subRoof,
    bottomFaces,
    roofTopFaces,
    roofTopPlates,
    roofingMaterialFaces,
    ridgeVentFaces,
    ridgeCapFaces,
    bellyBandBoards,
    wallBaseBoards,
    wallBaseBoardRails,
  };
}

interface PerformOverhangClippingOptions {
  readonly subRoof: GeneratedRoof;
  readonly subRoofs: Table<GeneratedRoof>;
  readonly roofTopFaces: RoofFace[];
}

interface PerformOverhangClippingResult {
  readonly startCapFaces: Face[];
  readonly endCapFaces: Face[];
  readonly startEaveExtensions: EaveExtension[];
  readonly endEaveExtensions: EaveExtension[];
  readonly roofTopClippingBSPs: (NodeBSP | null)[];
  readonly roofTopFaces: RoofFace[];
}

function performOverhangClipping({
  subRoof,
  subRoofs,
  roofTopFaces,
}: PerformOverhangClippingOptions): PerformOverhangClippingResult {
  const { wallFaces } = subRoof;
  const { startWallFaces, endWallFaces } = wallFaces;

  const startCapFaces: Face[] = [];
  const endCapFaces: Face[] = [];
  const startEaveExtensions: EaveExtension[] = [];
  const endEaveExtensions: EaveExtension[] = [];
  const finalRoofTopFaces: RoofFace[] = [];

  // helper functions for clipping the overhangs
  const clipOverhang = (roofTopClippingBSPs: (NodeBSP | null)[], endWallsClippingBSPs: (NodeBSP | null)[]) => {
    const roofTopOptions: ClipFaceContext = {
      clippingNodes: roofTopClippingBSPs.map((x) => clippingNodeNew(x)),
      coplanarsToFront: true,
      epsilon: MULTIGABLE_BSP_EPSILON,
    };

    const endWallOptions: ClipFaceContext = {
      clippingNodes: endWallsClippingBSPs.map((x) => clippingNodeNew(x)),
      coplanarsToFront: true,
      epsilon: MULTIGABLE_BSP_EPSILON,
    };

    // clip the roof top faces
    const nonClippedFaces = roofTopFaces.filter((x) => x.disableOverhangClipping);
    const clippedRoofTopFaces = clipRoofFaces(
      roofTopFaces.filter((x) => !x.disableOverhangClipping),
      roofTopOptions,
    );

    finalRoofTopFaces.push(...clippedRoofTopFaces);
    finalRoofTopFaces.push(...nonClippedFaces);

    // clip wall extensions
    clipRoofWallFaces(startWallFaces, endWallOptions);
    clipRoofWallFaces(endWallFaces, endWallOptions);
  };

  // collect the overhang clipping BSPs for this sub-roof
  const roofTopClippingBSPs = collectOverhangClipping({
    subRoof,
    subRoofs,
    isEaveClipping: true,
  });

  const endWallsClippingBSPs = collectOverhangClipping({
    subRoof,
    subRoofs,
    isWallClipping: true,
    filter: (_subRoof: GeneratedRoof, _side: RoofSide, otherSubRoof: GeneratedRoof, otherSide: RoofSide) => {
      return otherSubRoof.suppressWallExtension && (otherSide === RoofSide.START || otherSide === RoofSide.END);
    },
  });

  // clipping
  clipOverhang(roofTopClippingBSPs, endWallsClippingBSPs);

  return {
    startCapFaces,
    endCapFaces,
    startEaveExtensions,
    endEaveExtensions,
    roofTopClippingBSPs,
    roofTopFaces: finalRoofTopFaces,
  };
}

function buildRoofWallFacesOverlapBSPs(
  roofWallFaces: RoofWallFaces,
  wallSettings: WallSettings,
  roofWallOverlapBSPs: NodeBSP[] = [],
): NodeBSP[] {
  const offsetZ = MULTIGABLE_BSP_EPSILON * 2;

  const addFaceToBSP = (face: Face, thickness: number) => {
    const { loops, plane } = face;

    if (loops.length === 0 || polyLine2Area(loops[0]) < DIST_EPSILON_SQUARED) {
      return;
    }

    const offsetPlane = planeCopyMoved(plane, 0, 0, offsetZ);
    const [nX, nY, nZ] = planeNormal(offsetPlane);
    const pl3 = polyLine2OnPlane(offsetPlane, loops[0]);

    const polygons: PolygonBSP[] = [];
    for (let i = 0, l = pl3.length - 3; i < l; i += 3) {
      polygons.push(
        buildEdgePolygon(
          pl3[i],
          pl3[i + 1],
          pl3[i + 2],
          pl3[i + 3],
          pl3[i + 4],
          pl3[i + 5],
          -nX * thickness,
          -nY * thickness,
          -nZ * thickness,
        ) as PolygonBSP,
      );
    }

    const oppositePlane = planeMoveAlongNormal(planeCopyReversed(offsetPlane), thickness);

    buildPolygonsFromPolyline2(loops[0], offsetPlane, 0, polygons);

    buildPolygonsFromPolyline2(
      planeReProjectPolyLine2(loops[0], offsetPlane, oppositePlane),
      oppositePlane,
      0,
      polygons,
    );

    roofWallOverlapBSPs.push(addPolygonsToBSP(null, null, polygons, MULTIGABLE_BSP_EPSILON) as NodeBSP);
  };

  const addFacesToBSP = (faces: Face[], thickness: number) => {
    for (const face of faces) {
      addFaceToBSP(face, thickness);
    }
  };

  const { exteriorWallBaseThickness } = wallSettings;
  addFacesToBSP(roofWallFaces.rightFaces, exteriorWallBaseThickness);

  return roofWallOverlapBSPs;
}

export function buildGableWallOverlapBSPs(
  subRoofs: Table<GeneratedRoof>,
  wallSettings: WallSettings,
  roofWallOverlapBSPs: NodeBSP[] = [],
): NodeBSP[] {
  for (const roofId in subRoofs) {
    const { wallFaces } = subRoofs[roofId];
    const { startWallFaces, endWallFaces } = wallFaces;

    buildRoofWallFacesOverlapBSPs(startWallFaces, wallSettings, roofWallOverlapBSPs);
    buildRoofWallFacesOverlapBSPs(endWallFaces, wallSettings, roofWallOverlapBSPs);
  }

  return roofWallOverlapBSPs;
}
